import type { ReactNode, Ref } from 'react';
import { Box, Card, Typography } from '@mui/material';

export interface KeyValueRow {
  /** Row label; rendered UPPERCASE. */
  label: string;
  /** Row value (string or arbitrary node). */
  value: ReactNode;
  /** Render the value in a monospace face (account #, IMEI, ids). */
  mono?: boolean;
  /** Small trailing hint next to the value. */
  hint?: ReactNode;
  /** Allow the value to wrap across lines instead of truncating (long prose). */
  wrap?: boolean;
}

export interface KeyValueCardProps {
  /** Optional card header title. */
  title?: string;
  /** The definition rows. */
  rows: KeyValueRow[];
  /** Optional header actions (right-aligned). */
  actions?: ReactNode;
  /** Number of value columns in the grid (defaults to 1). */
  columns?: number;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Definition-list card for entity detail (line, user, invoice). Each row is a
 * label / value pair; values can be monospace and carry a trailing hint.
 */
export function KeyValueCard({ title, rows, actions, columns = 1, ref }: KeyValueCardProps) {
  return (
    <Card ref={ref} variant="outlined" sx={{ overflow: 'hidden' }}>
      {(title || actions) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            p: '14px 18px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {title && (
            <Typography
              component="div"
              sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}
            >
              {title}
            </Typography>
          )}
          {actions}
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {rows.map((row, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 2,
              p: '11px 18px',
              borderBottom: '1px solid',
              borderColor: 'borderSubtle',
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                color: 'text.secondary',
                flexShrink: 0,
              }}
            >
              {row.label}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 1,
                minWidth: 0,
                justifyContent: 'flex-end',
                textAlign: 'right',
              }}
            >
              <Typography
                component="span"
                title={typeof row.value === 'string' ? row.value : undefined}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'text.primary',
                  ...(row.wrap
                    ? { whiteSpace: 'normal', overflowWrap: 'break-word' }
                    : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
                  ...(row.mono ? { fontFamily: 'var(--font-mono, ui-monospace, monospace)' } : {}),
                }}
              >
                {row.value}
              </Typography>
              {row.hint && (
                <Typography
                  component="span"
                  sx={{ fontSize: 10, color: 'text.secondary', flexShrink: 0 }}
                >
                  {row.hint}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  );
}
